import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import EditorJS from '@editorjs/editorjs';
import * as Header from '@editorjs/header';
import * as List from '@editorjs/list';
import * as Paragraph from '@editorjs/paragraph';
import * as ImageTool from '@editorjs/image';
import { UploadService } from './upload.service';

@Component({
  selector: 'app-upload',
  standalone: true,
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.scss'],
  imports: [CommonModule, ReactiveFormsModule],
})
export class UploadComponent implements OnInit, AfterViewInit, OnDestroy {
  uploadForm!: FormGroup;
  fileType = 'document';
  editor!: EditorJS;
  editorData: any;

  products: any[] = [];
  modules: any[] = [];
  users: any[] = [];

  isUploading = false;
  selectedVideo: File | null = null;
  selectedDocs: File[] = [];
  selectedThumbnail: File | null = null;
  thumbnailPreview: string | null = null;
  thumbnailTouched = false;
  videoTouched = false;
  formSubmitted = false;
  showToast = false;
  toastMessage = '';

  @ViewChild('editorHolder', { static: false })
  editorHolderRef!: ElementRef;

  constructor(private fb: FormBuilder, private uploadService: UploadService) {}

  ngOnInit(): void {
    this.uploadForm = this.fb.group({
      product: ['', Validators.required],
      module: ['', Validators.required],
      title: ['', Validators.required],
      description: ['', Validators.required],
      tags: [''],
      createdBy: ['', Validators.required],
      fileType: ['document', Validators.required],
      question: [''],
      answer: [''],
    });

    this.loadProjects();
    this.loadUsers();
  }

  ngAfterViewInit(): void {
    if (this.fileType === 'document') this.initializeEditor();
  }

  // ==================== DATA LOADERS ====================
  loadProjects(): void {
    this.uploadService.getProjects().subscribe({
      next: (res) => (this.products = res),
      error: (err) => console.error('Error fetching projects:', err),
    });
  }

  loadUsers(): void {
    this.uploadService.getUsers().subscribe({
      next: (res) => {
        this.users = res.filter((user) => user?.raw?.status === 1);
      },
      error: (err) => console.error('Error fetching users:', err),
    });
  }

  onProductChange(event: any): void {
    const projectId = event.target.value;
    if (!projectId) return;
    this.uploadService.getModules(projectId).subscribe({
      next: (res) => (this.modules = res),
      error: (err) => console.error('Error fetching modules:', err),
    });
  }

  // ==================== FILE TYPE HANDLER ====================
  onFileTypeChange(type: string): void {
    this.fileType = type;
    this.uploadForm.patchValue({ fileType: type });

    // Reset validation + files
    ['title','description','tags','question','answer'].forEach(f => {
      this.uploadForm.get(f)?.clearValidators();
      this.uploadForm.get(f)?.updateValueAndValidity();
    });

    if (type === 'document') {
      this.uploadForm.get('title')?.setValidators(Validators.required);
      this.uploadForm.get('description')?.setValidators(Validators.required);
      setTimeout(() => this.initializeEditor(), 0);
    }

    if (type === 'faq') {
      this.uploadForm.get('question')?.setValidators(Validators.required);
      this.uploadForm.get('answer')?.setValidators(Validators.required);
      this.destroyEditor();
    }

    if (type === 'video') {
      this.uploadForm.get('title')?.setValidators(Validators.required);
      this.uploadForm.get('description')?.setValidators(Validators.required);
      this.destroyEditor();
    }

    this.uploadForm.updateValueAndValidity();
  }

  // ==================== EDITOR ====================
  initializeEditor(): void {
    if (this.editor) return;

    this.editor = new EditorJS({
      holder: this.editorHolderRef.nativeElement,
      placeholder: 'Start writing your document...',
      tools: {
        header: { class: (Header as any).default },
        paragraph: { class: (Paragraph as any).default },
        list: { class: (List as any).default },
        image: {
          class: (ImageTool as any).default,
          config: {
            uploader: {
              // ✅ Upload image to backend (which uploads to Azure)
              uploadByFile: async (file: File) => {
                const formData = new FormData();
                formData.append('projectId', this.uploadForm.value.product);
                formData.append('image', file);

                try {
                  const response = await fetch(
                    'http://localhost:3000/api/documents/upload-image',
                    {
                      method: 'POST',
                      body: formData,
                    }
                  );
                  const data = await response.json();

                  if (data?.success === 1 && data?.file?.url) {
                    return data; // Editor.js expects { success: 1, file: { url } }
                  } else {
                    console.error('Image upload failed:', data);
                    return { success: 0 };
                  }
                } catch (error) {
                  console.error('Upload error:', error);
                  return { success: 0 };
                }
              },
            },
          },
        },
      },
      data: this.editorData || {
        blocks: [
          { type: 'header', data: { text: 'Document Title', level: 1 } },
          { type: 'paragraph', data: { text: 'Start writing your content...' } },
        ],
      },
    });
  }

  // async saveDocument(): Promise<void> {
  //   if (!this.editor) return;
  //   try {
  //     const output = await this.editor.save();
  //     this.editorData = output;
  //     console.log('Document saved:', output);
  //   } catch (e) {
  //     console.error(e);
  //   }
  // }

  destroyEditor(): void {
    if (this.editor) {
      this.editor.destroy();
      this.editor = undefined as any;
    }
  }

  // ==================== FILE HANDLERS ====================
  onThumbnailSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      const file = input.files[0];
      if (!file.type.startsWith('image/')) return;
      this.selectedThumbnail = file;
      const reader = new FileReader();
      reader.onload = (e) => (this.thumbnailPreview = e.target?.result as string);
      reader.readAsDataURL(file);
    }
  }

  onThumbnailDrop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files[0];
    if (file && file.type.startsWith('image/')) {
      this.selectedThumbnail = file;
      const reader = new FileReader();
      reader.onload = (e) => (this.thumbnailPreview = e.target?.result as string);
      reader.readAsDataURL(file);
    }
  }

  onVideoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      const file = input.files[0];
      if (!file.type.startsWith('video/')) return;
      this.selectedVideo = file;
    }
  }

  onVideoDrop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files[0];
    if (file && file.type.startsWith('video/')) {
      this.selectedVideo = file;
    }
  }

  onAttachedDocumentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const newFiles = Array.from(input.files);
      this.selectedDocs = [...this.selectedDocs, ...newFiles];
    }
  }

  onDocDrop(event: DragEvent): void {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files) {
      const newFiles = Array.from(files);
      this.selectedDocs = [...this.selectedDocs, ...newFiles];
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  removeThumbnail(): void {
    this.selectedThumbnail = null;
    this.thumbnailPreview = null;
  }

  removeVideo(): void {
    this.selectedVideo = null;
  }

  removeDoc(index: number): void {
    this.selectedDocs.splice(index, 1);
  }

  // ==================== SUBMIT ====================
  onSubmit(): void {
    this.uploadForm.markAllAsTouched();

    if (this.uploadForm.invalid) return;
    // if (this.fileType === 'video' && (!this.selectedThumbnail || !this.selectedVideo)) return;

    this.formSubmitted = true;
    this.isUploading = true;

    if (this.fileType === 'faq') {
      this.uploadFaqToServer();
      return;
    }

    if (this.fileType === 'video') {
      this.uploadVideoToServer();
    } else {
      this.uploadDocumentToServer();
    }
  }

  private uploadFaqToServer(): void {
    const payload = {
      project: this.uploadForm.value.product,
      module: this.uploadForm.value.module,
      question: this.uploadForm.value.question,
      answer: this.uploadForm.value.answer,
      createdBy: this.uploadForm.value.createdBy,
    };

    this.uploadService.uploadFaq(payload).subscribe({
      next: () => {
        this.isUploading = false;
        this.uploadForm.reset({ fileType: 'faq' });
        this.showSuccessToast('FAQ uploaded successfully!');
      },
      error: err => {
        this.isUploading = false;
        console.error('FAQ upload failed', err);
      }
    });
  }


  private uploadVideoToServer(): void {
    const formData = new FormData();
    formData.append('video', this.selectedVideo!);
    formData.append('thumbnail', this.selectedThumbnail!);
    this.selectedDocs.forEach((doc) => formData.append('supporting', doc));

    formData.append('title', this.uploadForm.value.title);
    formData.append('description', this.uploadForm.value.description);
    formData.append('project', this.uploadForm.value.product);
    formData.append('projectModule', this.uploadForm.value.module);
    formData.append('createdBy', this.uploadForm.value.createdBy);
    formData.append('tags', this.uploadForm.value.tags || '');

    this.isUploading = true;
    this.uploadService.uploadVideo(formData).subscribe({
      next: () => {
        this.isUploading = false;
        this.uploadForm.reset();
        this.selectedVideo = null;
        this.selectedDocs = [];
        this.selectedThumbnail = null;
        this.thumbnailPreview = null;
        this.showSuccessToast('Video uploaded successfully!');
      },
      error: (err) => {
        this.isUploading = false;
        console.error('Upload error:', err);
      },
    });
  }

  private async uploadDocumentToServer(): Promise<void> {
    try {
      if (this.editor) {
        const output = await this.editor.save();
        this.editorData = output;
      }
console.log('📝 Captured document JSON before upload:', this.editorData);

      const formData = new FormData();
      if (this.selectedThumbnail) {
        formData.append('thumbnail', this.selectedThumbnail);
      }

      formData.append('title', this.uploadForm.value.title);
      formData.append('description', this.uploadForm.value.description);
      formData.append('project', this.uploadForm.value.product);
      formData.append('projectModule', this.uploadForm.value.module);
      formData.append('createdBy', this.uploadForm.value.createdBy);
      formData.append('tags', this.uploadForm.value.tags || '');
      formData.append('docData', JSON.stringify(this.editorData));

      this.isUploading = true;
      this.uploadService.uploadDocument(formData).subscribe({
        next: (res) => {
          this.isUploading = false;
          this.uploadForm.reset();
          this.selectedVideo = null;
          this.selectedDocs = [];
          this.selectedThumbnail = null;
          this.thumbnailPreview = null;
          this.editorData = null;
          this.destroyEditor();
          setTimeout(() => this.initializeEditor(), 0);
          this.showSuccessToast('Document uploaded successfully!');
        },
        error: (err) => {
          this.isUploading = false;
          console.error('❌ Document upload failed:', err);
        },
      });
    } catch (error) {
      this.isUploading = false;
      console.error('Error saving editor data:', error);
    }
  }

  // ==================== UTIL ====================
  formatBytes(bytes: number, decimals = 1): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  }

  showSuccessToast(message: string): void {
    this.toastMessage = message;
    this.showToast = true;

    // Hide toast automatically after 3 seconds
    setTimeout(() => {
      this.showToast = false;
    }, 3000);
  }

  ngOnDestroy(): void {
    this.destroyEditor();
  }
}
